import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export type JobType = "sprite-pack" | null;

interface JobState {
  type: JobType;
  title?: string;
  running: boolean;
  paused: boolean;
  completed: number;
  total: number;
  currentItem?: string;
  onStop?: () => void;
  onResume?: () => void;
  characterId?: string;
}

const JobContext = createContext<{
  job: JobState;
  startJob: (config: { type: Exclude<JobType, null>; title?: string; total: number; characterId?: string; onStop?: () => void; onResume?: () => void }) => void;
  updateProgress: (deltaCompleted: number) => void;
  setCurrentItem: (currentItem?: string) => void;
  setPaused: (paused: boolean) => void;
  clearJob: () => void;
}>({
  job: { type: null, running: false, paused: false, completed: 0, total: 0, characterId: undefined },
  startJob: () => {},
  updateProgress: () => {},
  setCurrentItem: () => {},
  setPaused: () => {},
  clearJob: () => {},
});

export function JobProvider({ children }: { children: React.ReactNode }) {
  const [job, setJob] = useState<JobState>({ type: null, running: false, paused: false, completed: 0, total: 0 });

  const startJob = useCallback((config: { type: Exclude<JobType, null>; title?: string; total: number; characterId?: string; onStop?: () => void; onResume?: () => void }) => {
    setJob({ type: config.type, title: config.title, characterId: config.characterId, running: true, paused: false, completed: 0, total: config.total, onStop: config.onStop, onResume: config.onResume });
  }, []);

  const updateProgress = useCallback((deltaCompleted: number) => {
    setJob((prev) => ({ ...prev, completed: Math.max(0, Math.min(prev.total, prev.completed + deltaCompleted)) }));
  }, []);

  const setCurrentItem = useCallback((currentItem?: string) => {
    setJob((prev) => ({ ...prev, currentItem }));
  }, []);

  const setPaused = useCallback((paused: boolean) => {
    setJob((prev) => ({ ...prev, paused, running: !paused }));
  }, []);

  const clearJob = useCallback(() => {
    setJob({ type: null, running: false, paused: false, completed: 0, total: 0 });
  }, []);

  const value = useMemo(() => ({ job, startJob, updateProgress, setCurrentItem, setPaused, clearJob }), [job, startJob, updateProgress, setCurrentItem, setPaused, clearJob]);

  return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
}

export function useJob() {
  return useContext(JobContext);
}
